import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, resolvePhoto, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Sparkles, Loader2, MoreVertical, Flag, Ban, X, MessageSquare
} from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import CoachPanel from "@/components/CoachPanel";

export default function ChatView({ match, onBack, onLeave }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [iceLoading, setIceLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimer = useRef(null);

  const qplus = !!user?.billing?.quietplus;

  // Load history once
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/matches/${match.id}/messages`)
      .then(({ data }) => mounted && setMessages(data))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [match.id]);

  // Open WebSocket
  useEffect(() => {
    const token = localStorage.getItem("rdv_token");
    if (!token || !match?.id) return;
    const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/chat/${match.id}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "message") {
          setMessages((prev) => {
            const i = prev.findIndex((m) => m.id === data.message.id);
            if (i !== -1) {
              const next = prev.slice();
              next[i] = { ...data.message, pending: false };
              return next;
            }
            return [...prev, data.message];
          });
        } else if (data.type === "typing") {
          if (data.user_id !== user?.id) {
            setOtherTyping(true);
            setTimeout(() => setOtherTyping(false), 1800);
          }
        }
      } catch (err) {
        console.warn("WS message parse failed:", err);
      }
    };
    return () => {
      try { ws.close(); } catch (err) {
        console.warn("WS close error:", err);
      }
    };
  }, [match?.id, user?.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, ideas, otherTyping]);

  const sendViaWS = (body, clientId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "message", text: body, client_id: clientId }));
      return true;
    }
    return false;
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body) return;
    setText("");
    setIdeas([]);
    const clientId = (crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const optimistic = {
      id: clientId,
      match_id: match.id,
      sender_id: user.id,
      text: body,
      created_at: new Date().toISOString(),
      pending: true,
    };
    setMessages((m) => [...m, optimistic]);
    if (sendViaWS(body, clientId)) return;
    // Fallback to REST
    try {
      const { data } = await api.post(`/matches/${match.id}/messages`, { text: body });
      setMessages((m) => m.map((x) => (x.id === clientId ? { ...data, pending: false } : x)));
    } catch {
      toast.error("Message didn't send");
      setMessages((m) => m.filter((x) => x.id !== clientId));
      setText(body);
    }
  };

  const handleType = (v) => {
    setText(v);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      if (typingTimer.current) return;
      wsRef.current.send(JSON.stringify({ type: "typing" }));
      typingTimer.current = setTimeout(() => { typingTimer.current = null; }, 1500);
    }
  };

  const fetchIcebreakers = async () => {
    setIceLoading(true);
    try {
      const { data } = await api.post(`/ai/icebreakers/${match.id}`);
      setIdeas(data.suggestions || []);
      if (!data.suggestions?.length) toast.error("No ideas just now");
    } catch {
      toast.error("AI editor is resting");
    } finally {
      setIceLoading(false);
    }
  };

  const block = async () => {
    if (!window.confirm(`Block ${other.name}? They won't see you again.`)) return;
    try {
      await api.post(`/users/${other.id}/block`);
      toast.success("Blocked");
      setMenuOpen(false);
      onLeave?.();
    } catch {
      toast.error("Couldn't block");
    }
  };

  const submitReport = async (reason) => {
    if (!reason?.trim()) return;
    try {
      await api.post(`/users/${other.id}/report`, { reason });
      toast.success("Report submitted. We've also blocked them for you.");
      setReportOpen(false);
      setMenuOpen(false);
      onLeave?.();
    } catch {
      toast.error("Couldn't submit report");
    }
  };

  const other = match.with_user;

  return (
    <div className="flex flex-col h-full" data-testid="chat-view">
      <header className="relative flex items-center gap-3 px-5 py-4 border-b border-[#E5DFD3] bg-white/60">
        <button data-testid="chat-back" onClick={onBack}
          className="md:hidden w-9 h-9 rounded-full border border-[#E5DFD3] flex items-center justify-center">
          <ArrowLeft size={16} />
        </button>
        <div className="w-11 h-11 rounded-full overflow-hidden bg-[#F0EBE1] flex-shrink-0">
          {other.photos?.[0] ? (
            <img src={resolvePhoto(other.photos[0])} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-serif text-lg">{other.name?.[0]}</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-xl text-[#1A1818] leading-none flex items-center gap-1.5" data-testid="chat-name">
            <span>{other.name}</span>
            {other.verified && <VerifiedBadge />}
          </div>
          <div className="text-xs text-[#737373] mt-1 flex items-center gap-2" data-testid="chat-status">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? "bg-[#4caf50]" : "bg-[#737373]"}`} />
            {connected ? "live" : "offline"} &middot; {other.location || "Somewhere lovely"}
          </div>
        </div>

        <button data-testid="coach-open" onClick={() => setCoachOpen(true)}
          className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
            qplus
              ? "bg-[#F3E8DD] border-[#D4A373]/40 hover:bg-[#EADBC8] text-[#913831]"
              : "border-[#E5DFD3] text-[#737373] hover:bg-[#F0EBE1]"
          }`} title="AI Dating Coach (Quiet+)">
          <MessageSquare size={14} />
        </button>

        <button data-testid="chat-menu-btn" onClick={() => setMenuOpen((v) => !v)}
          className="w-9 h-9 rounded-full border border-[#E5DFD3] hover:bg-[#F0EBE1] flex items-center justify-center">
          <MoreVertical size={14} />
        </button>

        {menuOpen && (
          <div className="absolute top-16 right-4 z-20 bg-white border border-[#E5DFD3] rounded-2xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] py-2 w-56" data-testid="chat-menu">
            <button data-testid="menu-report" onClick={() => { setReportOpen(true); setMenuOpen(false); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F0EBE1] flex items-center gap-3">
              <Flag size={14} className="text-[#913831]" /> Report
            </button>
            <button data-testid="menu-block" onClick={block}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F0EBE1] flex items-center gap-3 text-[#D9534F]">
              <Ban size={14} /> Block
            </button>
          </div>
        )}
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-3" data-testid="chat-messages">
        {loading ? (
          <div className="text-center text-[#737373] font-serif italic">loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-[#737373] py-10">
            <div className="font-serif text-2xl text-[#1A1818]">Say something specific.</div>
            <p className="text-sm mt-2">Reference something from {other.name}&apos;s profile. Or let Claude draft an opener.</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-snug ${
                  mine
                    ? "bg-[#913831] text-white rounded-2xl rounded-tr-md"
                    : "bg-[#F0EBE1] text-[#1A1818] rounded-2xl rounded-tl-md"
                } ${m.pending ? "opacity-70" : ""}`}>
                  {m.text}
                </div>
              </div>
            );
          })
        )}

        {otherTyping && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="bg-[#F0EBE1] text-[#737373] rounded-2xl rounded-tl-md px-4 py-2.5 text-sm font-serif italic">
              {other.name} is typing…
            </div>
          </div>
        )}

        {ideas.length > 0 && (
          <div className="mt-4 p-4 bg-[#F3E8DD]/60 border border-[#D4A373]/30 rounded-2xl" data-testid="icebreaker-ideas">
            <div className="text-xs uppercase tracking-[0.2em] text-[#913831] mb-2 flex items-center gap-1.5">
              <Sparkles size={12} /> Drafted by Claude
            </div>
            <div className="space-y-2">
              {ideas.map((i, idx) => (
                <button key={idx} data-testid={`idea-${idx}`} onClick={() => setText(i)}
                  className="block text-left w-full px-3 py-2 rounded-xl bg-white/60 hover:bg-white border border-[#E5DFD3] text-sm text-[#1A1818]">
                  {i}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="p-4 border-t border-[#E5DFD3] bg-white/60" data-testid="chat-form">
        <div className="flex items-center gap-2">
          <button type="button" data-testid="icebreakers-btn" onClick={fetchIcebreakers} disabled={iceLoading}
            className="w-11 h-11 rounded-full bg-[#F3E8DD] hover:bg-[#EADBC8] border border-[#D4A373]/40 flex items-center justify-center text-[#913831] disabled:opacity-50"
            title="Icebreakers from Claude">
            {iceLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          </button>
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => handleType(e.target.value)}
            placeholder={`Write to ${other.name}…`}
            className="flex-1 bg-white border border-[#E5DFD3] rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#913831]"
          />
          <button type="submit" data-testid="chat-send" disabled={!text.trim()}
            className="w-11 h-11 rounded-full bg-[#913831] hover:bg-[#7A2E28] text-white flex items-center justify-center disabled:opacity-50">
            <Send size={16} />
          </button>
        </div>
      </form>

      {reportOpen && (
        <ReportModal name={other.name} onClose={() => setReportOpen(false)} onSubmit={submitReport} />
      )}

      {coachOpen && (
        <CoachPanel matchId={match.id} otherName={other.name} isQuietPlus={qplus} onClose={() => setCoachOpen(false)} />
      )}
    </div>
  );
}

function ReportModal({ name, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const presets = [
    "Inappropriate photos",
    "Harassment or abusive messages",
    "Catfishing / fake profile",
    "Spam or scam",
    "Underage",
  ];
  return (
    <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
         onClick={onClose} data-testid="report-overlay">
      <div className="bg-white rounded-3xl border border-[#E5DFD3] p-6 sm:p-7 max-w-md w-full"
           onClick={(e) => e.stopPropagation()} data-testid="report-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#913831]">Report</div>
            <h3 className="font-serif text-3xl text-[#1A1818] mt-1">Report {name}</h3>
          </div>
          <button data-testid="report-close" onClick={onClose}
            className="w-9 h-9 rounded-full border border-[#E5DFD3] hover:bg-[#F0EBE1] flex items-center justify-center">
            <X size={14} />
          </button>
        </div>
        <p className="text-sm text-[#737373] mt-3">Reports are confidential. We&apos;ll also block them on your behalf.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {presets.map((p) => (
            <button key={p} data-testid={`report-preset-${p}`} type="button"
              onClick={() => setReason(p)}
              className={`text-xs px-3 py-1.5 rounded-full border ${
                reason === p ? "bg-[#1A1818] text-white border-[#1A1818]"
                  : "bg-white text-[#1A1818] border-[#E5DFD3] hover:bg-[#F0EBE1]"
              }`}>
              {p}
            </button>
          ))}
        </div>
        <textarea
          data-testid="report-textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="Add detail (optional)…"
          className="mt-4 w-full bg-[#FAF7F2] border border-[#E5DFD3] rounded-2xl p-4 text-sm focus:outline-none focus:border-[#913831] resize-none"
        />
        <button data-testid="report-submit" onClick={() => onSubmit(reason)}
          disabled={!reason.trim()}
          className="mt-5 w-full bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-50 text-white rounded-full py-3 font-medium">
          Submit report
        </button>
      </div>
    </div>
  );
}
